import { type Editor, useEditorState } from '@tiptap/react';
import { Toggle } from './ui/toggle';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import {
  Bold,
  Italic,
  Link,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  TextQuote,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

function LinkButton({
  editor,
  isActive,
}: {
  editor: Editor;
  isActive: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');

  const handleToggle = () => {
    if (isActive) {
      const currentHref = (editor.getAttributes('link').href as string) ?? '';
      setUrl(currentHref);
    } else {
      setUrl('');
    }
    setOpen(true);
  };

  const handleApply = () => {
    if (!url) return;
    const { selection } = editor.state;

    if (isActive) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    } else if (selection.empty) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href: url } }],
        })
        .run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
    setOpen(false);
    setUrl('');
  };

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setOpen(false);
    setUrl('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Toggle
              size={'sm'}
              pressed={isActive}
              onPressedChange={handleToggle}
              onMouseDown={(e) => e.preventDefault()}
              aria-label="Link"
              className="cursor-pointer aria-pressed:bg-zinc-200! aria-pressed:text-zinc-900! dark:aria-pressed:bg-zinc-700! dark:aria-pressed:text-zinc-50!"
            >
              <Link className="w-4 h-4" />
            </Toggle>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Link</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80 p-2">
        <div className="flex gap-2 items-center">
          <Input
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApply();
              }
            }}
            autoFocus
          />
          <Button size={'sm'} onClick={handleApply} className="cursor-pointer">
            {isActive ? 'Update' : 'Apply'}
          </Button>
          {isActive && (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleRemove}
              className="cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ToolbarButton({
  label,
  icon: Icon,
  isActive,
  onToggle,
}: {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Toggle
          size={'sm'}
          pressed={isActive}
          onPressedChange={onToggle}
          onMouseDown={(e) => e.preventDefault()}
          aria-label={label}
          className="cursor-pointer aria-pressed:bg-zinc-200! aria-pressed:text-zinc-900! dark:aria-pressed:bg-zinc-700! dark:aria-pressed:text-zinc-50!"
        >
          <Icon className="h-4 w-4" />
        </Toggle>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export default function Toolbar({ editor }: { editor: Editor | null }) {
  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive('bold') ?? false,
      isItalic: editor?.isActive('italic') ?? false,
      isH1: editor?.isActive('heading', { level: 1 }) ?? false,
      isH2: editor?.isActive('heading', { level: 2 }) ?? false,
      isH3: editor?.isActive('heading', { level: 3 }) ?? false,
      isBulletList: editor?.isActive('bulletList') ?? false,
      isOrderedList: editor?.isActive('orderedList') ?? false,
      isBlockquote: editor?.isActive('blockquote') ?? false,
      isLink: editor?.isActive('link') ?? false,
    }),
  });

  if (!editor) return null;

  const {
    isBold = false,
    isItalic = false,
    isH1 = false,
    isH2 = false,
    isH3 = false,
    isBulletList = false,
    isOrderedList = false,
    isBlockquote = false,
    isLink = false,
  } = editorState ?? {};

  return (
    <TooltipProvider delayDuration={700}>
      <div className="flex items-center gap-1 border-b border-input p-1">
        <ToolbarButton
          label={'Heading 1'}
          icon={Heading1}
          isActive={isH1}
          onToggle={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        />
        <ToolbarButton
          label={'Heading 2'}
          icon={Heading2}
          isActive={isH2}
          onToggle={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        />
        <ToolbarButton
          label={'Heading 3'}
          icon={Heading3}
          isActive={isH3}
          onToggle={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        />
        <Separator orientation="vertical" className="h-4!" />
        <ToolbarButton
          label={'Bold'}
          icon={Bold}
          isActive={isBold}
          onToggle={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          label={'Italic'}
          icon={Italic}
          isActive={isItalic}
          onToggle={() => editor.chain().focus().toggleItalic().run()}
        />
        <Separator orientation="vertical" className="h-4!" />
        <ToolbarButton
          label={'Bullet List'}
          icon={List}
          isActive={isBulletList}
          onToggle={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          label={'Ordered List'}
          icon={ListOrdered}
          isActive={isOrderedList}
          onToggle={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolbarButton
          label={'Blockquote'}
          icon={TextQuote}
          isActive={isBlockquote}
          onToggle={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <Separator orientation="vertical" className="h-4!" />
        <LinkButton editor={editor} isActive={isLink} />
      </div>
    </TooltipProvider>
  );
}
